import React, { useContext, useEffect, useState } from 'react';
import { Button, Row, Card, Image, Tooltip, Typography, Col, Modal, Pagination, Switch } from 'antd';
import { DeleteOutlined, EditOutlined, FolderAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import { useAddons } from '../../hooks/useMenuQuery';

const { Meta } = Card;
const { Title } = Typography;

function Addons() {
  const navigate = useNavigate();
  const { setHeaderComponent, isMobile } = useContext(LayoutContext);
  const [selectedItemDelete, setSelectedItemToDelete] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [togglingAddonIds, setTogglingAddonIds] = useState([]);

  const { doFetch: fetchDelete } = useFetch();
  const { doFetch: fetchUpdateAddon } = useFetch();
  const menuId = getMenuId();

  const { data: addonsData, isLoading, isFetching, error, refetch } = useAddons(menuId, currentPage, pageSize);

  useEffect(() => {
    setHeaderComponent('Addons');
    return () => setHeaderComponent(null);
  }, []);

  const handleItemDelete = () => {
    fetchDelete({
      url: URLS.deleteAddon({ addonId: selectedItemDelete.id }),
      method: 'DELETE',
      onSuccess: () => {
        refetch();
        setSelectedItemToDelete();
      },
    });
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleAddonStockToggle = (addon, checked) => {
    setTogglingAddonIds(prev => (prev.includes(addon.id) ? prev : [...prev, addon.id]));
    fetchUpdateAddon({
      url: URLS.updateAddon({ addonId: addon.id }),
      method: 'PUT',
      params: {
        is_in_stock: checked,
      },
      onSuccess: () => {
        setTogglingAddonIds(prev => prev.filter(id => id !== addon.id));
        refetch();
      },
      onError: () => {
        setTogglingAddonIds(prev => prev.filter(id => id !== addon.id));
      },
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading addons...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <div>Error loading addons: {error.message}</div>
      </div>
    );
  }

  const addons = addonsData?.addons || [];

  return (
    <div className="addons-page-container">
      {isFetching && !isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            zIndex: 9999,
            background: 'linear-gradient(90deg, #1890ff 25%, #69c0ff 50%, #1890ff 75%)',
            backgroundSize: '200% 100%',
            animation: 'loading 1.5s ease infinite',
          }}
        />
      )}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={14}>
          <Title level={4}>Addons ({addonsData?.total || 0})</Title>
        </Col>
        <Col xs={24} sm={10}>
          <Row gutter={[8, 8]} justify={isMobile ? 'center' : 'end'}>
            <Col>
              <Button type="primary" icon={<FolderAddOutlined />} onClick={() => navigate('/addon-create')}>
                Create addon
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        {addons.map(addon => {
          const primaryName =
            addon.addon_translations?.[0]?.name_text || `Addon #${addon.id}`;
          const nameHtml =
            addon.addon_translations?.reduce((acc, tr) => {
              acc += `${tr.language_id}: ${tr.name_text}<br />`;
              return acc;
            }, '') || '';

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={addon.id}>
              <Card
                cover={
                  <Image
                    alt="addon"
                    src={addon.image_url}
                    fallback={getImageFallbackUrl(addon.image_url)}
                    style={{ height: 160, objectFit: 'cover' }}
                  />
                }
                actions={[
                  <Tooltip title="Edit" key="edit">
                    <EditOutlined onClick={() => navigate(`/addon/${addon.id}`)} />
                  </Tooltip>,
                  <Tooltip title="Delete" key="del">
                    <DeleteOutlined onClick={() => setSelectedItemToDelete(addon)} />
                  </Tooltip>,
                ]}
              >
                <Meta
                  title={
                    <Tooltip title={<div dangerouslySetInnerHTML={{ __html: nameHtml }} />}>
                      {primaryName}
                    </Tooltip>
                  }
                  description={
                    <>
                      {addon.price} {addon.currency}
                      <div style={{ marginTop: 8 }}>
                        <span style={{ marginRight: 8 }}>Enabled</span>
                        <Switch
                          checked={addon.is_in_stock}
                          loading={togglingAddonIds.includes(addon.id)}
                          onChange={checked => handleAddonStockToggle(addon, checked)}
                        />
                      </div>
                      {!addon.is_in_stock && (
                        <span style={{ color: '#cf1322', marginLeft: 8 }}>Out of stock</span>
                      )}
                    </>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {(addonsData?.pages || 0) > 1 && (
        <Row justify="center" style={{ marginTop: 24 }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={addonsData?.total || 0}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </Row>
      )}

      <Modal
        title="Delete addon"
        open={!!selectedItemDelete}
        onOk={handleItemDelete}
        onCancel={() => setSelectedItemToDelete()}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Remove this addon from the menu? Products will be unlinked automatically.
      </Modal>
    </div>
  );
}

export default Addons;
